// Beat Arranger Constants — Section 12
import { defaultTrackEffects } from '@/state/trackModel';

/** Stored color for section markers created from imported lyrics (saved with the project). */
export const LYRICS_MARKER_COLOR = '#00D4AA';

export const INSTRUMENT_COLORS = {
  drums: '#FF2D78',
  melody: '#00D4AA',
  bass: '#39FF14',
  texture: '#B44DFF',
  fx: '#FF8A00',
  vocals: '#4DA6FF',
};

/**
 * LTBB classroom colors (taught this way since 2019): melodies purple, drums blue, textures
 * yellow, sound effects green. Normal sessions keep INSTRUMENT_COLORS; class-pack tracks
 * use the matching --ba-class-* token strings from state/classSession.js.
 */
export const CLASS_COLORS = {
  melody: '#A855F7',
  drums: '#3B82F6',
  texture: '#FACC15',
  fx: '#22C55E',
};

/** For buttons that are not one of the four class instruments (e.g. Pick a vibe). */
export const NEUTRAL_BUTTON_COLOR = '#E5E7EB';

export const INSTRUMENT_ORDER = ['drums', 'melody', 'bass', 'texture', 'fx', 'vocals'];

export const GRID = {
  PX_PER_BAR: 52,
  TRACK_HEIGHT: 68,
  TRACK_HEADER_WIDTH: 340,
  LIBRARY_WIDTH: 260,
  RULER_HEIGHT: 50,       // loop strip (14) + bar numbers (36)
  LOOP_STRIP_HEIGHT: 14,
  CLIP_HEIGHT: 56,
  STATUSBAR_HEIGHT: 30,
  TOPNAV_HEIGHT: 64,
};

export const TOOLS = ['Select', 'Slice', 'Copy'];

export const BPM_RANGE = { min: 60, max: 200 };
export const BARS_RANGE = { min: 8, max: 80 };
export const MAX_TRACKS = 32;
export const MAX_PROJECTS = 15;

// Placeholder track/clip data for Phase 2 visual shell
export const PLACEHOLDER_TRACKS = [
  {
    id: 't1',
    name: 'Boom Bap Kick',
    instrument: 'drums',
    color: '#FF2D78',
    volume: 80,
    pan: 0,
    muted: false,
    solo: false,
    effects: defaultTrackEffects(),
    clips: [
      { id: 'c1', loop_id: 'loop-001', name: 'Boom Bap Kick', color: '#FF2D78', start_bar: 0, bars: 4, fade_in: 0, fade_out: 0 },
      { id: 'c2', loop_id: 'loop-001', name: 'Boom Bap Kick', color: '#FF2D78', start_bar: 8, bars: 4, fade_in: 0, fade_out: 0 },
      { id: 'c3', loop_id: 'loop-001', name: 'Boom Bap Kick', color: '#FF2D78', start_bar: 16, bars: 8, fade_in: 0, fade_out: 0 },
    ],
  },
  {
    id: 't2',
    name: 'Chill Piano Chords',
    instrument: 'melody',
    color: '#00D4AA',
    volume: 70,
    pan: -10,
    muted: false,
    solo: false,
    effects: defaultTrackEffects(),
    clips: [
      { id: 'c4', loop_id: 'loop-003', name: 'Chill Piano', color: '#00D4AA', start_bar: 4, bars: 8, fade_in: 0, fade_out: 0 },
      { id: 'c5', loop_id: 'loop-003', name: 'Chill Piano', color: '#00D4AA', start_bar: 16, bars: 8, fade_in: 0, fade_out: 0 },
    ],
  },
  {
    id: 't3',
    name: '808 Sub Bass',
    instrument: 'bass',
    color: '#39FF14',
    volume: 85,
    pan: 0,
    muted: false,
    solo: false,
    effects: defaultTrackEffects(),
    clips: [
      { id: 'c6', loop_id: 'loop-004', name: '808 Sub', color: '#39FF14', start_bar: 0, bars: 8, fade_in: 0, fade_out: 0 },
      { id: 'c7', loop_id: 'loop-004', name: '808 Sub', color: '#39FF14', start_bar: 12, bars: 4, fade_in: 0, fade_out: 0 },
    ],
  },
  {
    id: 't4',
    name: 'Vinyl Crackle',
    instrument: 'texture',
    color: '#B44DFF',
    volume: 40,
    pan: 20,
    muted: false,
    solo: false,
    effects: defaultTrackEffects(),
    clips: [
      { id: 'c8', loop_id: 'loop-005', name: 'Vinyl Crackle', color: '#B44DFF', start_bar: 0, bars: 16, fade_in: 0, fade_out: 0 },
    ],
  },
  {
    id: 't5',
    name: 'Riser FX',
    instrument: 'fx',
    color: '#FF8A00',
    volume: 60,
    pan: 0,
    muted: true,
    solo: false,
    effects: defaultTrackEffects(),
    clips: [
      { id: 'c9', loop_id: 'loop-fx', name: 'Riser', color: '#FF8A00', start_bar: 14, bars: 2, fade_in: 0, fade_out: 0 },
    ],
  },
];

export const PLACEHOLDER_SECTION_MARKERS = [
  { name: 'Intro', startBar: 0, color: '#00D4AA' },
  { name: 'Verse', startBar: 8, color: '#B44DFF' },
  { name: 'Drop', startBar: 16, color: '#FF2D78' },
  { name: 'Outro', startBar: 24, color: '#FF8A00' },
];

export const PLACEHOLDER_LOOPS = [
  { id: 'loop-001', name: 'Boom Bap Kick', bpm: 90, key: '-', instrument_group: 'drums', bars: 2, energy: 3, moods: ['Confident', 'Hip Hop'], pack_code: 'pack-hiphop-01', audio_url: '' },
  { id: 'loop-002', name: 'Trap Hi-Hats', bpm: 140, key: '-', instrument_group: 'drums', bars: 2, energy: 4, moods: ['Trap', 'Energetic'], pack_code: 'pack-trap-01', audio_url: '' },
  { id: 'loop-003', name: 'Chill Piano Chords', bpm: 85, key: 'Cm', instrument_group: 'melody', bars: 4, energy: 2, moods: ['Chill', 'Lo-Fi'], pack_code: 'pack-lofi-01', audio_url: '' },
  { id: 'loop-004', name: '808 Sub Bass', bpm: 140, key: 'Fm', instrument_group: 'bass', bars: 2, energy: 4, moods: ['Trap', 'Dark'], pack_code: 'pack-trap-01', audio_url: '' },
  { id: 'loop-005', name: 'Vinyl Crackle', bpm: 90, key: '-', instrument_group: 'texture', bars: 8, energy: 1, moods: ['Lo-Fi', 'Nostalgic'], pack_code: 'pack-lofi-01', audio_url: '' },
  { id: 'loop-006', name: 'Dreamy Pad', bpm: 80, key: 'Am', instrument_group: 'texture', bars: 4, energy: 2, moods: ['Dreamy', 'Ambient'], pack_code: 'pack-lofi-01', audio_url: '' },
  { id: 'loop-007', name: 'Funk Guitar Riff', bpm: 100, key: 'Em', instrument_group: 'melody', bars: 2, energy: 3, moods: ['Funky', 'Groovy'], pack_code: 'pack-hiphop-01', audio_url: '' },
  { id: 'loop-008', name: 'Snare Roll', bpm: 140, key: '-', instrument_group: 'drums', bars: 1, energy: 5, moods: ['Trap', 'Build'], pack_code: 'pack-trap-01', audio_url: '' },
];

export const PLACEHOLDER_PACKS = [
  { id: 'pack-hiphop-01', name: 'Hip Hop Essentials', artwork_url: '', loop_count: 24 },
  { id: 'pack-trap-01', name: 'Trap Heat', artwork_url: '', loop_count: 20 },
  { id: 'pack-lofi-01', name: 'Lo-Fi Vibes', artwork_url: '', loop_count: 18 },
];
